import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Btn, Card, Field, inputCls, Modal } from "@/components/common";
import { AlertTriangle, Save } from "lucide-react";
import { DEFAULT_DOC_TEXTS } from "@/lib/docTexts";
import SpokiSettings from "@/components/SpokiSettings";

export default function Settings() {
  const [s, setS] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [resetting, setResetting] = useState(false);

  useEffect(() => { api.get("/settings").then((r) => setS(r.data)); }, []);
  if (!s) return null;

  const save = async () => {
    try {
      await api.put("/settings", {
        name: s.name, subtitle: s.subtitle, currency: s.currency,
        doc_nb_text: s.doc_nb_text || "", voucher_privacy_text: s.voucher_privacy_text || "",
        ricevuta_policy_text: s.ricevuta_policy_text || "", ricevuta_policy_url: s.ricevuta_policy_url || "",
      });
      toast.success("Impostazioni salvate");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const doReset = async () => {
    setResetting(true);
    try {
      const { data } = await api.post("/admin/reset");
      toast.success(`Dati azzerati (${Object.values(data.deleted).reduce((a, b) => a + b, 0)} record eliminati)`);
      setConfirmOpen(false);
      setConfirmText("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setResetting(false); }
  };

  return (
    <div>
      <PageHeader title="Impostazioni" subtitle="Configurazione generale">
        <Btn data-testid="save-settings" onClick={save}><Save size={16} /> Salva impostazioni</Btn>
      </PageHeader>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="p-5 space-y-4">
          <h3 className="font-heading font-bold text-slate-900">Generale</h3>
          <Field label="Nome struttura">
            <input value={s.name || ""} onChange={(e) => setS({ ...s, name: e.target.value })} className={inputCls} data-testid="set-name" />
          </Field>
          <Field label="Sottotitolo">
            <input value={s.subtitle || ""} onChange={(e) => setS({ ...s, subtitle: e.target.value })} className={inputCls} data-testid="set-subtitle" />
          </Field>
          <Field label="Valuta base">
            <input value={s.currency || "EUR"} disabled className={`${inputCls} bg-slate-100`} />
          </Field>
          <p className="text-xs text-slate-500">
            Le commissioni delle agenzie si gestiscono nella pagina <b>Agenzie</b>.
          </p>
        </Card>

        <Card className="p-5 space-y-4 lg:col-span-2">
          <div>
            <h3 className="font-heading font-bold text-slate-900">Testi legali dei documenti</h3>
            <p className="text-xs text-slate-500 mt-1">Compaiono su Voucher e Ricevuta (singoli e di gruppo). Lascia vuoto per usare il testo predefinito.</p>
          </div>
          <Field label="Nota N.B. (Voucher + Ricevuta)">
            <textarea rows={3} value={s.doc_nb_text || ""} onChange={(e) => setS({ ...s, doc_nb_text: e.target.value })} className={inputCls} placeholder={DEFAULT_DOC_TEXTS.nb} data-testid="set-doc-nb" />
          </Field>
          <Field label="Testo privacy / titolare del trattamento (Voucher)">
            <textarea rows={5} value={s.voucher_privacy_text || ""} onChange={(e) => setS({ ...s, voucher_privacy_text: e.target.value })} className={inputCls} placeholder={DEFAULT_DOC_TEXTS.privacy} data-testid="set-voucher-privacy" />
          </Field>
          <Field label="Testo politiche di prenotazione (Ricevuta)">
            <textarea rows={3} value={s.ricevuta_policy_text || ""} onChange={(e) => setS({ ...s, ricevuta_policy_text: e.target.value })} className={inputCls} placeholder={DEFAULT_DOC_TEXTS.policyText} data-testid="set-ricevuta-policy-text" />
          </Field>
          <Field label="Link politiche (Ricevuta)">
            <input value={s.ricevuta_policy_url || ""} onChange={(e) => setS({ ...s, ricevuta_policy_url: e.target.value })} className={inputCls} placeholder={DEFAULT_DOC_TEXTS.policyUrl} data-testid="set-ricevuta-policy-url" />
          </Field>
        </Card>

        <SpokiSettings />

        <Card className="p-5 border-red-200 bg-red-50/40">
          <h3 className="font-heading font-bold text-red-600 flex items-center gap-2">
            <AlertTriangle size={18} /> Danger zone
          </h3>
          <p className="text-sm text-slate-600 mt-2 mb-4">
            Azzera tutti i dati operativi: proprietà, ospiti, prenotazioni ed extra.
            Mantiene il tuo account, le agenzie e le impostazioni. <b>Azione irreversibile.</b>
          </p>
          <button
            data-testid="reset-data-btn"
            onClick={() => setConfirmOpen(true)}
            className="px-4 py-2 rounded-md border border-red-300 text-red-600 font-semibold text-sm hover:bg-red-100 transition-colors duration-150"
          >
            Azzera tutti i dati
          </button>
        </Card>
      </div>

      <Modal open={confirmOpen} onClose={() => setConfirmOpen(false)} title="Conferma azzeramento dati">
        <div className="space-y-4">
          <p className="text-sm text-slate-600">
            Verranno eliminati definitivamente <b>proprietà, ospiti, prenotazioni e voci di spesa</b>.
            Scrivi <b>AZZERA</b> per confermare.
          </p>
          <input
            data-testid="reset-confirm-input"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            className={inputCls}
            placeholder="AZZERA"
          />
          <div className="flex justify-end gap-2">
            <Btn type="button" variant="secondary" onClick={() => setConfirmOpen(false)}>Annulla</Btn>
            <button
              data-testid="reset-confirm-btn"
              disabled={confirmText !== "AZZERA" || resetting}
              onClick={doReset}
              className="px-4 py-2 rounded-md bg-red-600 text-white font-semibold text-sm hover:bg-red-700 disabled:opacity-50 transition-colors duration-150"
            >
              {resetting ? "Azzeramento…" : "Azzera definitivamente"}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
